import { useLife } from '@/state/life';
import { useMarkets } from '@/state/markets';
import { usePower } from '@/state/power';
import { useXClone } from '@/state/xclone';
import { usePhone } from '@/state/phone';
import { useNotifications } from '@/state/notifications';
import { useCinematic } from '@/components/CinematicTakeover';
import { useMeta } from '@/state/meta';
import { sounds } from '@/audio/synth';
import { PHONE_ACTION_HANDLERS } from './phoneActions';

/**
 * Charity Gala mini-arc.
 *
 * Available when player owns the Charity Foundation. Costs $50K to host.
 * Three schmoozing moments → tribal rep gains, networking, IRS heat
 * reduction (it is a "real" charity event after all).
 */

interface Moment {
  text: string;
  options: { label: string; effect: () => void }[];
}

const MOMENTS: Moment[] = [
  {
    text: 'A TECH BILLIONAIRE corners you near the open bar. "we should swap notes on regulators."',
    options: [
      { label: '"definitely. let me get my lawyer."', effect: () => {
        usePower.getState().bumpInfluence(8);
        useLife.getState().bumpFbiHeat(2);
      }},
      { label: '"i don\'t talk shop at events."', effect: () => {
        usePower.getState().bumpRep('press', 4);
      }},
      { label: 'pretend you didn\'t hear and walk to the dessert table', effect: () => {
        useLife.getState().bumpSanity(2);
      }},
    ],
  },
  {
    text: 'YOUR WIFE leans over: "the senator\'s wife just asked if we summer in Aspen. what do i say?"',
    options: [
      { label: '"yes — we\'re looking at properties."', effect: () => {
        usePower.getState().bumpInfluence(4);
        useLife.getState().bumpRelationship('wife', -3);
      }},
      { label: '"the truth — we summer wherever the wifi works."', effect: () => {
        useLife.getState().bumpRelationship('wife', 8);
        usePower.getState().bumpRep('press', 3);
      }},
      { label: '"introduce her to the Saudi prince."', effect: () => {
        usePower.getState().bumpInfluence(10);
        useLife.getState().bumpRelationship('wife', -10);
        useLife.getState().bumpFbiHeat(5);
      }},
    ],
  },
  {
    text: 'THE GALA AUCTIONEER: "and now, a private lunch with [a controversial public figure]. opening bid: \$200K."',
    options: [
      { label: 'bid \$200K', effect: () => {
        useMarkets.getState().addCash(-200_000);
        usePower.getState().bumpInfluence(15);
        usePower.getState().bumpRep('press', -4);
      }},
      { label: 'bid \$50K', effect: () => {
        useMarkets.getState().addCash(-50_000);
        usePower.getState().bumpRep('cnbc', 4);
      }},
      { label: 'duck out before he calls your name', effect: () => {
        useLife.getState().bumpSanity(4);
      }},
    ],
  },
];

export function startCharityGalaArc(): void {
  useMeta.getState().recordLore('arc-charity-gala', 'Charity Gala');
  useCinematic.getState().fire({
    id: `gala-${Date.now()}`,
    type: 'breaking',
    headline: 'CHARITY GALA — TONIGHT.',
    subhead: 'your foundation hosts. \$50K to put on. networking value: incalculable. tax deductible value: most of it.',
    durationMs: 5000,
  });
  useMarkets.getState().addCash(-50_000);
  useLife.getState().bumpIrsHeat(-15); // "art" deduction
  sounds.notifyAlert();

  for (let i = 0; i < MOMENTS.length; i++) {
    const m = MOMENTS[i]!;
    setTimeout(() => {
      const ids = ['a', 'b', 'c'];
      const actions = m.options.map((opt, idx) => ({
        id: ids[idx]!,
        label: opt.label,
        effectId: `gala-q${i}-${ids[idx]}`,
      }));
      for (let j = 0; j < m.options.length; j++) {
        const id = `gala-q${i}-${ids[j]}`;
        const opt = m.options[j]!;
        PHONE_ACTION_HANDLERS[id] = () => {
          opt.effect();
          sounds.fill();
        };
      }
      usePhone.getState().receiveMessage({
        threadId: 'wife',
        fromNpc: 'wife',
        text: m.text,
        actions,
      });
      useNotifications.getState().push({ surfaceId: 'phone', severity: 'urgent', text: `gala: moment ${i + 1}/3` });
      sounds.notifyAlert();
    }, 5_000 + i * 35_000);
  }

  setTimeout(() => {
    useXClone.getState().receivePost({
      authorHandle: 'WSJ_RealTimeBait', authorTribe: 'press',
      text: 'BREAKING: charity gala hosted by a "well-known retail trader" raised \$2.4M for "the Foundation". attendees included multiple unindicted public figures.',
      likes: 7400, reposts: 1900,
    });
  }, 5_000 + MOMENTS.length * 35_000 + 5_000);
}
